import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.jsx";
import { landingFor } from "../../lib/roles.js";

/**
 * ContributePhotos — a Contributor's photo surface.
 *
 * Deliberately NOT the coordinator media library. That page is 632 lines of
 * bulk selection, drag-select, per-image delete, tagging and placement
 * management — every destructive control on it requires CAP_EDIT, so a
 * Contributor clicking one would collect a 403. Retrofitting it would mean
 * threading a permission flag through four nested components and risking the
 * flow coordinators depend on.
 *
 * More to the point, it's the wrong tool. Someone invited because they know the
 * person wants to add a few photographs and say what's happening in them. They
 * do not want a tag manager.
 *
 * So: pick photos, add a caption, done. Uploads land in the same media library
 * the Organizer curates, which is the point — the Contributor supplies raw
 * material and the Organizer decides where it appears.
 */
export default function ContributePhotos() {
  const { clientId }  = useParams();
  const { apiFetch, user } = useAuth();
  const navigate      = useNavigate();
  const fileRef       = useRef(null);

  const [clientName, setClientName] = useState("");
  const [images, setImages]   = useState(null);
  const [queue, setQueue]     = useState([]);      // [{file, caption, status}]
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [notice, setNotice]   = useState(null);
  const [dragging, setDragging] = useState(false);

  async function load() {
    try {
      const [c, m] = await Promise.all([
        apiFetch(`/api/clients/${clientId}`),
        apiFetch(`/api/clients/${clientId}/media`),
      ]);
      if (c.ok) setClientName((await c.json()).display_name || "");
      if (m.ok) {
        const data = await m.json();
        setImages(Array.isArray(data) ? data : (data.images || []));
      }
    } catch (e) {
      if (!e.sessionEnded) setError("We couldn't load this page just now.");
    }
  }

  useEffect(() => { load(); }, [clientId]);

  function addFiles(fileList) {
    const picked = Array.from(fileList || [])
      .filter(f => f.type.startsWith("image/"))
      .map(f => ({ file: f, caption: "", status: "ready" }));
    if (picked.length) setQueue(q => [...q, ...picked]);
  }

  function setCaption(i, value) {
    setQueue(q => q.map((item, idx) => idx === i ? { ...item, caption: value } : item));
  }

  function removeFromQueue(i) {
    setQueue(q => q.filter((_, idx) => idx !== i));
  }

  async function uploadAll() {
    setBusy(true); setError(null); setNotice(null);
    let done = 0, repeats = 0;

    for (let i = 0; i < queue.length; i++) {
      const item = queue[i];
      if (item.status === "done" || item.status === "duplicate") continue;
      const body = new FormData();
      body.append("file", item.file);
      if (item.caption.trim()) body.append("caption", item.caption.trim());
      try {
        const r = await apiFetch(`/api/clients/${clientId}/media`, { method: "POST", body });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || "Upload failed");
        if (d.duplicate) {
          // Already in the library (Oct 25) — nothing was added; say so on the row.
          repeats += 1;
          setQueue(q => q.map((it, idx) => idx === i ? { ...it, status: "duplicate" } : it));
          continue;
        }
        done += 1;
        setQueue(q => q.map((it, idx) => idx === i ? { ...it, status: "done" } : it));
      } catch (e) {
        if (e.sessionEnded) { setBusy(false); return; }
        setQueue(q => q.map((it, idx) =>
          idx === i ? { ...it, status: "failed", error: e.message } : it));
      }
    }

    setBusy(false);
    if (repeats && !done) {
      setNotice(repeats === 1
        ? "That photo is already in the library, so it wasn't added again."
        : `Those ${repeats} photos are already in the library, so they weren't added again.`);
    }
    if (done) {
      const also = repeats
        ? ` ${repeats === 1 ? "One was" : `${repeats} were`} already in the library, so not added again.`
        : "";
      setNotice((done === 1
        ? "Photo added. Thank you — it's in the photo library, and the coordinator chooses where it appears."
        : `${done} photos added. Thank you — they're in the photo library, and the coordinator chooses where they appear.`) + also);
      setQueue(q => q.filter(it => it.status !== "done" && it.status !== "duplicate"));
      load();
    }
  }

  const pending = queue.filter(q => q.status !== "done" && q.status !== "duplicate");

  return (
    <div style={s.page}>
      <div style={s.content}>
        <Link to={landingFor(user)} style={s.back}>← Back</Link>

        <h1 style={s.title}>Add photos of {clientName || "them"}</h1>
        <p style={s.sub}>
          A photograph says things a description can't. Add any you'd want
          someone new to see, and tell us what's happening in it if you like.
        </p>

        {/* Drop zone */}
        <div
          style={{ ...s.drop, ...(dragging ? s.dropActive : {}) }}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={e => (e.key === "Enter" || e.key === " ") && fileRef.current?.click()}
        >
          <p style={s.dropTitle}>Drag photos here, or click to choose</p>
          <p style={s.dropHint}>JPEG, PNG, WebP or GIF · up to 10 MB each</p>
          <input ref={fileRef} type="file" accept="image/*" multiple
                 style={{ display: "none" }}
                 onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
        </div>

        {notice && <div style={s.notice}>{notice}</div>}
        {error  && <div style={s.error}>{error}</div>}

        {/* Queue */}
        {pending.length > 0 && (
          <>
            <div style={s.sectionLabel}>Ready to add</div>
            <div style={s.queue}>
              {queue.map((item, i) => item.status === "done" ? null : (
                <div key={i} style={s.queueRow}>
                  <img src={URL.createObjectURL(item.file)} alt="" style={s.queueThumb} />
                  <div style={s.queueMain}>
                    <div style={s.queueName}>{item.file.name}</div>
                    <input
                      style={s.captionInput}
                      placeholder="What's happening here? (optional)"
                      value={item.caption}
                      onChange={e => setCaption(i, e.target.value)}
                      disabled={busy}
                    />
                    {item.status === "failed" && (
                      <div style={s.rowError}>{item.error}</div>
                    )}
                    {item.status === "duplicate" && (
                      <div style={s.rowNote}>Already in the library — not added again.</div>
                    )}
                  </div>
                  <button style={s.removeBtn} onClick={() => removeFromQueue(i)}
                          disabled={busy} aria-label="Remove from list">×</button>
                </div>
              ))}
            </div>
            <button style={{ ...s.primaryBtn, ...(busy ? s.disabled : {}) }}
                    onClick={uploadAll} disabled={busy}>
              {busy ? "Adding…" : `Add ${pending.length} photo${pending.length !== 1 ? "s" : ""}`}
            </button>
          </>
        )}

        {/* Already there */}
        {/* Only her own (Oct 24): the server lists what she added, nothing else. */}
        <div style={s.sectionLabel}>Photos you've added</div>
        {images === null && <p style={s.muted}>Loading…</p>}
        {images?.length === 0 && (
          <p style={s.muted}>None yet.</p>
        )}
        {images && images.length > 0 && (
          <div style={s.grid}>
            {images.map(img => (
              <img key={img.id}
                   src={`/api/clients/${clientId}/media/${img.id}?v=thumb`}
                   alt={img.alt_text || ""} style={s.gridImg} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page:        { minHeight: "100dvh", background: "var(--color-bg-subtle)", padding: "40px 24px 80px" },
  content:     { maxWidth: 720, margin: "0 auto" },
  back:        { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", textDecoration: "none" },
  title:       { fontSize: "var(--text-2xl)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", letterSpacing: "-0.5px", margin: "16px 0 8px" },
  sub:         { fontSize: "var(--text-base)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", margin: "0 0 28px" },
  drop:        { border: "2px dashed var(--color-border)", borderRadius: "var(--radius-xl)", padding: "40px 24px", textAlign: "center", cursor: "pointer", background: "var(--color-surface)", transition: "border-color 150ms ease, background 150ms ease" },
  dropActive:  { borderColor: "var(--color-accent)", background: "var(--color-accent-subtle)" },
  dropTitle:   { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", margin: 0 },
  dropHint:    { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", margin: "6px 0 0" },
  sectionLabel:{ fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.06em", margin: "28px 0 10px" },
  queue:       { display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 },
  queueRow:    { display: "flex", gap: 12, alignItems: "flex-start", background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: 12 },
  queueThumb:  { width: 64, height: 64, objectFit: "cover", borderRadius: "var(--radius-sm)", flexShrink: 0 },
  queueMain:   { flex: 1, minWidth: 0 },
  queueName:   { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  captionInput:{ width: "100%", fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "8px 10px", background: "var(--color-bg-base)", color: "var(--color-text-primary)", fontFamily: "inherit", boxSizing: "border-box" },
  rowNote:     { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: 6 },
  rowError:    { fontSize: "var(--text-xs)", color: "var(--color-critical)", marginTop: 6 },
  removeBtn:   { background: "none", border: "none", fontSize: 20, color: "var(--color-text-tertiary)", cursor: "pointer", lineHeight: 1, padding: 4 },
  primaryBtn:  { background: "var(--color-accent)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", padding: "12px 22px", fontSize: "var(--text-base)", fontWeight: "var(--weight-semibold)", cursor: "pointer", font: "inherit" },
  disabled:    { opacity: 0.5, cursor: "not-allowed" },
  grid:        { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 8 },
  gridImg:     { width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: "var(--radius-sm)", background: "var(--color-border)" },
  notice:      { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "var(--color-accent-subtle)", border: "1px solid var(--color-accent)", borderRadius: "var(--radius-md)", padding: "10px 14px", marginTop: 16 },
  error:       { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-md)", padding: "10px 14px", marginTop: 16 },
  muted:       { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)" },
};
